import {
  useZenRowsByTransport,
  useZenRowsDaily,
  useZenRowsRecent,
  useZenRowsSummary,
} from '../api/queries';
import { ZenRowsSpendChart } from '../charts/ZenRowsSpendChart';
import { SERIES, STATUS } from '../charts/palette';
import { Badge } from '../components/Badge';
import { Card } from '../components/Card';
import { Failed, Loading } from '../components/States';
import { StatTile } from '../components/StatTile';
import { count, relative, when } from '../utils/format';
import { dollars, dollarsPrecise } from '../utils/usd';

/** Credits per call, by ZenRows' fixed and published multipliers. */
const MULTIPLIERS: { label: string; credits: number }[] = [
  { label: 'plain request', credits: 1 },
  { label: 'js_render', credits: 5 },
  { label: 'premium_proxy', credits: 10 },
  { label: 'both together', credits: 25 },
];

export function ZenRowsPage() {
  const summary = useZenRowsSummary();
  const daily = useZenRowsDaily(30);
  const transports = useZenRowsByTransport(30);
  const recent = useZenRowsRecent(25);

  const avg = summary.data?.avgCreditsPerRequest ?? null;
  const activeMultiplier = MULTIPLIERS.find((m) => avg !== null && Math.round(avg) === m.credits);

  return (
    <div className="stack">
      <header className="page__head">
        <div>
          <h1 className="page__title">ZenRows usage</h1>
          <p className="page__lede">
            What the paid transport has cost. ZenRows publishes no account or balance endpoint, so every
            figure here is accumulated from the billing headers returned on each call and stored as it
            happens.
          </p>
        </div>
        {summary.data?.concurrencyLimit !== null && summary.data !== undefined && (
          <Badge label={`Plan concurrency: ${summary.data.concurrencyLimit}`} color={SERIES[0]} />
        )}
      </header>

      {summary.isError ? (
        <Failed error={summary.error} />
      ) : summary.isLoading ? (
        <Loading label="Loading usage…" />
      ) : (
        summary.data && (
          <>
            <div className="grid grid--tiles">
              <StatTile
                label="Credits this cycle"
                value={count(summary.data.creditsThisCycle)}
                foot={`${summary.data.cycleStart} to ${summary.data.cycleEnd}`}
                accent={SERIES[1]}
              />
              <StatTile
                label="Cost this cycle"
                value={dollars(summary.data.costThisCycleUsd)}
                foot={`${count(summary.data.requestsThisCycle)} paid calls`}
                accent={SERIES[1]}
              />
              <StatTile
                label="Credits remaining"
                value={
                  summary.data.creditsRemaining === null ? 'not declared' : count(summary.data.creditsRemaining)
                }
                foot={
                  summary.data.creditsRemaining === null
                    ? 'set ZENROWS_MONTHLY_CREDITS to track this'
                    : `${summary.data.creditsRemainingPct}% of the declared allowance`
                }
                accent={
                  summary.data.creditsRemainingPct !== null && summary.data.creditsRemainingPct < 20
                    ? STATUS.critical
                    : STATUS.good
                }
              />
              <StatTile
                label="Projected this cycle"
                value={
                  summary.data.projectedCycleCredits === null
                    ? '—'
                    : count(summary.data.projectedCycleCredits)
                }
                foot="credits, at the current daily rate"
              />
              <StatTile
                label="Today"
                value={count(summary.data.creditsToday)}
                foot={`${dollars(summary.data.costTodayUsd)} spent`}
              />
              <StatTile
                label="Credits per call"
                value={avg === null ? '—' : String(avg)}
                foot={activeMultiplier ? `matches "${activeMultiplier.label}"` : 'mixed settings'}
                accent={avg !== null && avg >= 25 ? STATUS.warning : undefined}
              />
            </div>

            {summary.data.creditsRemaining === null && (
              <div className="banner">
                <span aria-hidden="true">◔</span>
                <div>
                  <strong>ZenRows does not expose a credit balance through its API.</strong>
                  <div className="muted" style={{ marginTop: 4 }}>
                    Their <code>/v1/usage</code>, <code>/v1/account</code>, <code>/v1/subscriptions</code> and{' '}
                    <code>/v1/stats</code> endpoints all return 404 — the balance exists only in the
                    dashboard. Set <code>ZENROWS_MONTHLY_CREDITS</code> to your plan's allowance and{' '}
                    <code>ZENROWS_BILLING_CYCLE_DAY</code> to its reset day, and "remaining" is computed from
                    what has actually been spent. It is derived, not authoritative.
                  </div>
                </div>
              </div>
            )}

            {avg !== null && avg >= 25 && (
              <div className="banner" style={{ borderLeftColor: STATUS.warning }}>
                <span aria-hidden="true">⚠</span>
                <div>
                  <strong>Each call is costing {avg} credits — the most expensive combination.</strong>
                  <div className="muted" style={{ marginTop: 4 }}>
                    <code>ZENROWS_JS_RENDER</code> and <code>ZENROWS_PREMIUM_PROXY</code> are both on, which
                    ZenRows bills at 25× a plain request. Worth testing whether{' '}
                    <code>premium_proxy</code> alone (10 credits) gets past Heureka — that would cut the bill
                    by 60% for two test requests' worth of effort.
                  </div>
                </div>
              </div>
            )}
          </>
        )
      )}

      <Card title="Credits per day" note="Last 30 days, with the share burned on failed calls">
        {daily.isError ? (
          <Failed error={daily.error} />
        ) : daily.isLoading ? (
          <Loading />
        ) : (
          <ZenRowsSpendChart points={daily.data ?? []} />
        )}
      </Card>

      <div className="grid grid--halves">
        <Card title="Spend by mode" note="Last 30 days">
          {transports.isError ? (
            <Failed error={transports.error} />
          ) : transports.isLoading ? (
            <Loading />
          ) : (transports.data?.length ?? 0) === 0 ? (
            <div className="state">No paid calls yet.</div>
          ) : (
            <div className="table-wrap">
              <table className="data">
                <thead>
                  <tr>
                    <th>Mode</th>
                    <th className="num">Calls</th>
                    <th className="num">Failed</th>
                    <th className="num">Credits</th>
                    <th className="num">Per call</th>
                    <th className="num">Cost</th>
                  </tr>
                </thead>
                <tbody>
                  {transports.data?.map((row) => (
                    <tr key={row.transport}>
                      <td>{row.transport}</td>
                      <td className="num">{count(row.requests)}</td>
                      <td className="num">{count(row.failures)}</td>
                      <td className="num">{count(row.credits)}</td>
                      <td className="num">{row.avgCreditsPerRequest}</td>
                      <td className="num">{dollars(row.costUsd)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        <Card title="What a call costs" note="ZenRows' fixed multipliers; these never change by plan">
          <div className="table-wrap">
            <table className="data">
              <thead>
                <tr>
                  <th>Settings</th>
                  <th className="num">Credits</th>
                  <th>In use</th>
                </tr>
              </thead>
              <tbody>
                {MULTIPLIERS.map((row) => (
                  <tr key={row.label}>
                    <td>{row.label}</td>
                    <td className="num">{row.credits}</td>
                    <td>
                      {activeMultiplier?.label === row.label ? (
                        <Badge label="yours" color={STATUS.warning} />
                      ) : (
                        <span className="muted">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      </div>

      <Card
        title="Recent paid calls"
        note={summary.data ? `${count(summary.data.totalCreditsAllTime)} credits recorded in total, ${dollars(summary.data.totalCostAllTimeUsd)}` : undefined}
      >
        {recent.isError ? (
          <Failed error={recent.error} />
        ) : recent.isLoading ? (
          <Loading />
        ) : (recent.data?.length ?? 0) === 0 ? (
          <div className="state">No paid calls recorded yet.</div>
        ) : (
          <div className="table-wrap">
            <table className="data">
              <thead>
                <tr>
                  <th>When</th>
                  <th>Mode</th>
                  <th className="num">Status</th>
                  <th className="num">Credits</th>
                  <th className="num">Cost</th>
                  <th className="num">Took</th>
                  <th className="num">Free slots</th>
                  <th>Target</th>
                </tr>
              </thead>
              <tbody>
                {recent.data?.map((row) => (
                  <tr key={row.id}>
                    <td className="secondary" title={when(row.createdAt)}>
                      {relative(row.createdAt)}
                    </td>
                    <td className="secondary">{row.transport}</td>
                    <td className="num" style={{ color: row.success ? undefined : STATUS.critical }}>
                      {row.status ?? '—'}
                    </td>
                    <td className="num">{row.credits ?? '—'}</td>
                    <td className="num">{dollarsPrecise(row.costUsd)}</td>
                    <td className="num secondary">
                      {row.durationMs === null ? '—' : `${(row.durationMs / 1000).toFixed(1)} s`}
                    </td>
                    <td className="num secondary">
                      {row.concurrencyRemaining === null
                        ? '—'
                        : `${row.concurrencyRemaining}/${row.concurrencyLimit}`}
                    </td>
                    <td
                      className="muted"
                      style={{ maxWidth: 320, overflow: 'hidden', textOverflow: 'ellipsis', fontSize: 11 }}
                      title={row.targetUrl ?? ''}
                    >
                      {row.targetUrl ? decodeURIComponent(row.targetUrl).replace('https://www.heureka.sk/', '') : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
